/**
 * English, the reference language: every key the app uses, with its text. A value is a string with
 * `{name}` placeholders, or a function for plurals. nl.ts must have the same keys.
 */
import { refusalsEn } from './refusals';
import type { Message } from './types';

const n = (p: Record<string, string | number>): number => Number(p['n'] ?? 0);

export const en = {
  'app.name': 'Terrella',

  // Tile kinds, as they appear in labels and buttons. Nothing creates fruit kinds, stone hills,
  // the old wood and stone machines or the planet infuser any more; their codes are reserved, so
  // they keep a name.
  'kind.stone': 'Stone',
  'kind.soil': 'Soil',
  'kind.grass': 'Grass',
  'kind.water': 'Water',
  'kind.sapling': 'Sapling',
  'kind.fruit-sapling': 'Fruit sapling',
  'kind.tree': 'Tree',
  'kind.fruit-tree': 'Fruit tree',
  'kind.hill': 'Stone hill',
  'kind.machine-wood': 'Wood machine',
  'kind.machine-stone': 'Stone machine',
  'kind.ore': 'Ore',
  'kind.infuser-ruin': 'Planet infuser, in ruins',
  'kind.infuser': 'Planet infuser',
  'kind.hub': 'Hub',
  // the deep layers (rules 19)
  'kind.basalt': 'Basalt',
  'kind.gold': 'Gold',
  'kind.core': 'Core',
  // rock a crystal charge broke, lying on its tile to be gathered (plan 34)
  'kind.broken': 'Broken {kind}',
  'group.tree': 'Forest',
  'group.water': 'Deep water',

  // Extractors: machines that draw a resource from the ground they stand on, a name per type
  'machine.crystal': 'Crystal resonator',
  'machine.crystal-many': 'crystal resonators',
  'machine.crystal-what': 'Stands on ore and draws crystal from it and from the ore around it.',
  'machine.stone': 'Stonecutter',
  'machine.stone-many': 'stonecutters',
  'machine.remove': 'Remove',
  'machine.remove-aria': 'Remove the {name}',
  'machine.stone-what': 'Stands on stone and cuts blocks from it and from the stone around it.',
  // Crystal charges (plan 34): bound to rock, they break it into rubble to gather
  'machine.charge': 'Crystal charge',
  'machine.charge-many': 'crystal charges',
  'machine.charge-what':
    'Bound to stone or ore, it hums for a few seconds, then breaks the rock under it into rubble you can gather.',
  'machine.heavy-charge': 'Heavy crystal charge',
  'machine.heavy-charge-many': 'heavy crystal charges',
  'machine.heavy-charge-what': 'Breaks basalt and gold too, and the rock all round it.',
  // the infuser (plan 36): lays blocks onto the ground round it
  'machine.infuser': 'Infuser',
  'machine.infuser-many': 'infusers',
  'machine.infuser-what':
    'Load it with blocks: it lays them on the tiles around it, lowest first. Ore makes ore ground, stone makes stone ground, soil fills a pit.',

  // Resources
  'resource.wood': 'Wood',
  'resource.stone': 'Stone',
  'resource.crystal': 'Crystal',
  // blocks, the layers gathered by hand (plan 34): countable, so each has its plural
  'resource.soil-block': 'Soil block',
  'resource.soil-block-many': 'Soil blocks',
  'resource.stone-block': 'Stone block',
  'resource.stone-block-many': 'Stone blocks',
  'resource.ore-block': 'Ore block',
  'resource.ore-block-many': 'Ore blocks',
  'resource.basalt-block': 'Basalt block',
  'resource.basalt-block-many': 'Basalt blocks',
  'resource.gold-block': 'Gold block',
  'resource.gold-block-many': 'Gold blocks',
  'resource.amount': '{n} {resource}',
  'resource.and': 'and',
  'resource.nothing': 'nothing',

  // What the tile you stand on can do
  'tile.depth': 'Depth {n} · ',
  'tile.height': 'Height {n} · ',
  'hint.ore': 'A crystal deposit: place a crystal resonator on it from the build menu.',
  'hint.stone': 'Bare stone: place a stonecutter on it, or break it with a crystal charge.',
  'hint.soil': 'Loose soil: gather it by hand.',
  'hint.basalt': 'Basalt: a heavy crystal charge breaks it.',
  'hint.gold': 'Gold, laid open: a heavy crystal charge breaks it.',
  'hint.core': 'The core: nothing breaks it.',
  'kind.rock': 'solid rock',
  'hint.broken': 'Gather it by hand.',
  'hint.fuse': 'Breaks the rock in {time}.',
  'hint.lay': '{block} lands in {time}.',
  'hint.load': 'Load it with blocks.',
  'hint.nowhere': 'Nowhere to lay: every tile round it is taken.',
  'hint.hub': 'Invite anyone with its QR code, join a world or make one.',
  'hint.sapling': 'Becomes a tree in {time}.',
  'hint.tree': 'Three together make a forest.',
  'hint.water': 'Three together make deep water.',
  'hint.drawn': (p) =>
    n(p) === 1
      ? `A ${String(p['name'])} draws from it.`
      : `${n(p)} ${String(p['names'])} share it.`,

  // The buttons of what stands on a tile
  'action.collect': 'Harvest',
  'action.collect-aria': 'Harvest {n} {resource} from the {name}',
  'action.collect-empty-aria': 'Harvest: the {name} holds nothing yet',
  'action.empty': 'nothing yet',
  'action.holds': '{n} {resource}',
  'action.holds-blocks': (p) => (n(p) === 1 ? '1 block' : `${n(p)} blocks`),
  // what fits in my pockets (rules 21)
  'action.fits': '{fit} of {n} fit',
  'action.full': 'pockets full',
  'action.waiting': 'waiting',
  'action.info': 'Values',
  'action.info-aria': 'The values of the {name}',
  'action.hub': 'Hub',
  'action.hub-sub': 'invite · travel',
  'action.hub-aria': 'Open the hub: invite anyone, join a world or make one',
  // gathering loose ground by hand (plan 34), and a charge's fuse
  'action.gather': 'Gather',
  'action.gather-aria': 'Gather a {block} from this tile',
  // loading an infuser with blocks (plan 36)
  'action.load': 'Load',
  'action.load-aria': 'Load your blocks into the {name}',
  'action.load-kinds': (p) => (n(p) === 1 ? '1 kind of block' : `${n(p)} kinds of block`),
  'action.load-none': 'no blocks',
  'action.hopper-full': 'hopper full',
  'load.title': 'Load the {name}',
  'load.pick-aria': 'Load your {block} into it',
  'load.note':
    'It lays them on the tiles around it in the order they go in, the lowest tile first.',

  // The HUD
  'hud.pockets-aria': 'You carry {wood} wood, {stone} stone and {crystal} crystal',
  'hud.admin': 'Admin',
  'hud.pockets-admin-aria': 'Admin mode: unlimited resources and pockets',
  'hud.canvas-aria':
    "Your world's planet. Drag or use the arrow keys to walk. The buttons below show what you can do where you stand; B opens the build menu.",
  'hud.you': 'You',
  'hud.crewmate': 'A player',
  'hud.machine': 'machine',
  'hud.chip-alone-aria':
    'World {world}: its code and settings. Nobody else is on the planet right now',
  'hud.chip-aria': (p) =>
    `World ${String(p['world'])}: its code and settings. ${String(p['names'])} ${n(p) === 1 ? 'is' : 'are'} on the planet now`,

  // The welcome screen
  'welcome.title': 'Welcome to Terrella',
  'welcome.lede':
    'Build up a small planet together: set machines on its ground and harvest what they draw from it.',
  'welcome.login': 'Sign in',
  'welcome.register': 'Create an account',
  'welcome.signed-in-as': 'Signed in as {name}',
  'welcome.guest-as': 'Playing as {name}, a guest',
  'welcome.logout': 'Sign out',
  'welcome.pick': 'Pick a world',
  'welcome.pick-text': "Join a friend's world with its code, or start a new one and share yours.",
  'welcome.join': 'Join a world',
  'welcome.join-text': 'Enter the code your friend gave you.',
  'welcome.code-placeholder': 'World code, like ABCD EFGH',
  'welcome.code-aria': 'World code',
  'welcome.join-btn': 'Join',
  'welcome.create': 'Create a world',
  'welcome.create-text': "Give it a name. You'll get a code to share with your friends.",
  'welcome.name-placeholder': 'World name, like Night Owls',
  'welcome.name-aria': 'World name',
  'welcome.create-btn': 'Create',
  'welcome.back': 'Back',
  'welcome.name-first': 'Give your world a name first.',
  'welcome.code-first': 'Enter the code first.',

  // The world panel
  'world.code': 'World code',
  'world.invite': 'Invite',
  'world.invite-hint': 'Whoever scans the code or opens the link is on this planet at once.',
  'world.qr-aria': "The world's QR code: tap to see it bigger",
  'world.copy-link': 'Copy link',
  'world.copied': 'Copied',
  'world.share': 'Share',
  'world.share-text': 'Come and play on {world} in Terrella',
  'world.first-name': "{name}'s world",

  // The hub: a pad on the planet where you invite anyone, join another world or create one
  'hub.title': 'The hub',
  'hub.invite-hint':
    'Anyone who scans the code above is on this planet at once: in the Terrella app, or in the browser as a guest.',
  'hub.guest-create': "You're playing as a guest. Create an account to make a world of your own.",
  'world.info': (p) => (Number(p['tiles']) === 1 ? '1 tile' : `${Number(p['tiles'])} tiles`),
  'world.testing': 'Testing',
  'world.lab': 'Tile lab',
  'world.skip': 'Skip 6 hours',
  'world.skip-done': '6 hours skipped, for everyone in the world.',
  'world.give': 'Fill my pockets',
  'world.give-done': 'Your pockets were filled.',
  'world.test-planet': 'Test planet',
  'world.test-planet-done': 'The planet was remade with a hole down to the core.',
  // admin mode (plan 35): unlimited resources and pockets, for testing
  'world.admin': 'Admin mode',
  'world.admin-on': 'Admin mode: everything is free and your pockets have no limit.',
  'world.admin-off': 'Admin mode is off: your pockets hold what they did, up to their room.',
  'world.testing-note': 'Only development builds show these. They change the planet for everyone.',
  'world.leave': 'Leave world',
  'world.leave-confirm': 'Leave {world}? Everything you grew stays on its planet.',
  'world.stay': 'Stay',

  // The build menu: everything you can build, then placing it where you stand
  'build.title': 'Build',
  'build.open': 'Build',
  'build.open-aria': 'Open the build menu',
  'build.pick-aria': 'Place a {name}',
  'build.free': 'Free',
  'place.confirm': 'Place',
  'place.confirm-aria': 'Place the {name} here',
  'place.cancel': 'Stop placing',

  // Refusals in words: a short reason for a button, a sentence per code (refusals.ts)
  ...refusalsEn,

  // The sheet about what stands on a tile
  'info.level': 'Level {n}',
  'info.holds': 'Holds',
  'info.intake': 'Takes in',
  'info.reach': 'Draws from',
  'info.per': 'per {period}',
  'info.tiles': (p) =>
    n(p) === 1 ? `${String(p['ground'])} tile` : `${String(p['ground'])} tiles`,
  'info.steps': (p) =>
    n(p) === 0 ? 'its own tile' : n(p) === 1 ? 'reach 1 step' : `reach ${n(p)} steps`,
  'info.shared': 'Ground it shares with another machine gives each its share.',
  'info.shared-tiles': (p) => (n(p) === 1 ? '1 shared' : `${n(p)} shared`),
  'info.full': 'Full: it takes nothing in until you harvest it.',
  // a crystal charge's values
  'info.fuse': 'Fuse',
  'info.fuse-left': 'Breaks in {time}',
  'info.breaks': 'Breaks',
  'info.area-own': 'the rock under it',
  'info.area-ring': 'the rock under it and all round it',
  'info.area': 'Area',
  // an infuser's values
  'info.hopper': 'Hopper',
  'info.blocks': 'blocks',
  'info.laying': 'Laying',
  'info.lay-what': '{block}, onto the lit tile',
  'info.idle-empty': 'Empty: load it with blocks.',
  'info.idle-nowhere': 'Nowhere to lay: every tile round it is full or taken.',
  'info.ring': 'Lays on',
  'info.ring-takes': (p) => (n(p) === 1 ? '1 tile takes a block' : `${n(p)} tiles take a block`),
  'info.tiles-broken': (p) => (n(p) === 1 ? '1 tile of rock' : `${n(p)} tiles of rock`),

  // A machine's levels and upgrades: a page of its Values sheet, a card per level
  'upgrade.title': 'Upgrades',
  'upgrade.open-aria': 'The upgrades of the {name}',
  'upgrade.available': 'Level {n} available',
  'upgrade.next-soon': 'Level {n} coming soon',
  'upgrade.top': 'At its highest level',
  'upgrade.past': 'Done',
  'upgrade.current': 'Current',
  'upgrade.next': 'Next',
  'upgrade.later': 'Later',
  'upgrade.after': 'After level {n}',
  'upgrade.soon': 'Coming soon',
  'upgrade.soon-text': 'Its next level is in the works.',
  'upgrade.takes': 'Takes {time}',
  'upgrade.button': 'Upgrade',
  'upgrade.button-aria': 'Upgrade the {name} to level {n}',
  'upgrade.underway': 'Becoming level {n}',
  'upgrade.left': '{time} left',
  'upgrade.working': 'It keeps working at its level until then.',

  // Who's on the planet
  'online.title': 'On the planet now',
  'online.others': 'Also in this world',
  'online.nobody': 'Nobody else is here right now.',
  'online.you': 'You',
  'online.changes': (p) => (n(p) === 1 ? '1 change' : `${n(p)} changes`),
  'online.here': 'here now',

  // While you were away
  'away.title': 'While you were away',
  'away.changes': (p) =>
    `${String(p['name'])} made ${n(p) === 1 ? '1 change' : `${n(p)} changes`} to the planet.`,
  'away.saplings': (p) =>
    n(p) === 1 ? '1 sapling grew into a tree.' : `${n(p)} saplings grew into trees.`,
  'away.fell': (p) => `Raised ground fell back ${n(p) === 1 ? 'once' : `${n(p)} times`}.`,
  'away.sank': (p) =>
    n(p) === 1 ? 'A pond sank to a lower shore.' : `${n(p)} ponds sank to a lower shore.`,
  'away.laid': (p) => (n(p) === 1 ? 'An infuser laid a block.' : `Infusers laid ${n(p)} blocks.`),
  'away.shattered': (p) =>
    n(p) === 1 ? 'A charge broke a tile of rock.' : `Charges broke ${n(p)} tiles of rock.`,

  // Toasts
  'toast.fell': 'Raised ground lost its support and fell back into stone.',
  'toast.sank': (p) =>
    `The water sank to level ${n(p) < 0 ? `−${-n(p)}` : String(n(p))}: it sits no higher than its highest shore.`,
  'toast.skipped': '{name} skipped 6 hours ahead.',
  'toast.collected': 'You harvested {n} {resource}.',
  'toast.upgrading': '{name} is upgrading the {machine} to level {n}.',
  'toast.upgraded': 'The {machine} is level {n} now.',
  'toast.removed': '{name} removed a {machine}.',
  'toast.joined': '{name} is on the planet.',
  'toast.member-joined': '{name} joined the world.',
  'toast.member-left': '{name} left the world.',
  'toast.offline': 'No connection. Your changes wait until it is back.',
  'toast.outdated': 'Terrella needs an update to keep playing.',
  'toast.rules-differ': 'The game rules changed. Update Terrella to see your changes right away.',

  'ui.close': 'Close',
  'ui.back': 'Back',

  // Durations
  'time.s': '{n} s',
  'time.min': '{n} min',
  'time.h': '{h} h',
  'time.hm': '{h} h {m} min',
} as const satisfies Record<string, Message>;

export type MessageKey = keyof typeof en;
